import { h } from 'preact';
import Store from 'store/store';
import * as actions from 'store/actions';

import { joinClasses } from 'utils/utils';
import { showTrailerModal } from 'utils/trailer';
import { isVideoAsset, getYoutubeId, isYoutubeLink } from 'utils/assets';
import { getButtons } from 'utils/buttons';

import ScrollList from 'components/scrollList/scrollList';
import PageLink from 'components/pageLink/pageLink';
import ArrowRight from 'components/icons/arrowRight';

import s from 'components/scrollList/moviesList.sss';

const onesheetErrors = new Set();

export default class MoviesList extends ScrollList {

	s = s;

	constructor(props) {
		super(props);

		this.state = {
			...this.state,
			currentItem: 0,
		};

		this.onOnesheetError = this.onOnesheetError.bind(this);
		this.onShowtimesAnimationEnd = this.onShowtimesAnimationEnd.bind(this);
		this.trackCarouselButtonClick = this.trackCarouselButtonClick.bind(this);
		this.trackNavigationClick = this.trackNavigationClick.bind(this);
		this.renderButton = this.renderButton.bind(this);
	}

	onOnesheetError(e) {
		onesheetErrors.add(e.target.src);
		// Since we keep track of errors outside the component, manually trigger a re-render
		this.forceUpdate();
		// Also ensure the scrollbar is accurate (TODO: less hacky way to trigger this after the render?)
		setTimeout(this.checkScrollPosition, 0);
	}

	checkScrollPosition() {
		super.checkScrollPosition();
		if (!this.$scroller) return;

		// Get the closes item when the scroll position changes
		// TODO: cache children and distances?
		const options = [...this.$scroller.children];
		const scrollPadding = Math.round(parseFloat(getComputedStyle(this.$scroller).paddingLeft));
		const currentScroll = this.$scroller.scrollLeft + scrollPadding;
		const distances = options.map(el => ({ el, dist: Math.abs(el.offsetLeft - currentScroll) }));
		const closest = distances.sort((a, b) => a.dist - b.dist)[0]?.el;
		const currentItem = options.indexOf(closest);
		if (currentItem !== this.state.currentItem) {
			this.setState({ currentItem });
		}
	}

	trackCarouselButtonClick(e) {
		const id = e.target.closest('[data-id]')?.dataset.id;
		const item = id && this.items.find(item => item.movie?.id === id);
		Store.emit(actions.CAROUSEL_BUTTON_CLICK, { title: item?.movie?.title, label: e.target.textContent });
	}

	trackNavigationClick(direction) {
		Store.emit(actions.CAROUSEL_TAB_DIRECTION_CLICK, { direction });
	}

	onShowtimesAnimationEnd(e) {
		if (e.animationName === s.showtimesTimer) {
			const id = e.target.closest('[data-id]')?.dataset.id;
			if (id) {
				const max = e.target.parentElement.children.length || 1;
				this.setState({ [`activeShowtime-${id}`]: ((this.state[`activeShowtime-${id}`] || 0) + 1) % max });
			}
		}
	}
	onShowtimesTransitionEnd(e) {
		if (e.propertyName === 'transform') {
			e.target.style.transition = 'none';
			e.target.style.transform = 'translateX(0)';
			e.target.clientWidth;
			e.target.style.transition = '';
			e.target.style.transform = '';
		}
	}

	renderTitleLink() {
		const titleLink = this.props.titleLink;
		if (!titleLink) return null;
		return (
			<PageLink
				to={titleLink.page} class={s.titleLink} onClick={() => {
					Store.emit(actions.UPCOMING_MOVIES_CLICK, {});
				}}
			>
				{titleLink.label}
				<svg width="13" viewBox="0 0 13 25" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M0.704262 1.78002C0.431262 1.48752 0.431262 1.01202 0.704262 0.719522C0.978762 0.427023 1.42126 0.427023 1.69576 0.719522L12.2948 11.968C12.5678 12.262 12.5678 12.7375 12.2948 13.03L1.69576 24.28C1.55926 24.427 1.37926 24.499 1.20076 24.499C1.02076 24.499 0.842262 24.427 0.704262 24.28C0.431262 23.9875 0.431262 23.512 0.704262 23.218L10.8203 12.499L0.704262 1.78002Z" fill="white" />
				</svg>
			</PageLink>
		);
	}

	renderTitle() {
		return (
			<div class={s.titleWrapper}>
				<h2 class={s.title}>{this.props.title}</h2>
				{this.renderTitleLink()}
			</div>
		);
	}

	getNextShowtimeLabel(showtime) {
		return showtime?.label || '';
	}

	getNextShowtimes(item) {
		const data = this.props.nextShowtimes?.[item?.movie?.id];
		if (!data) return null;

		return data.map((showtime, j) => {
			if (j > 0) return;
			const label = this.getNextShowtimeLabel(showtime);
			return (
				<div class={joinClasses(j === (this.state[`activeShowtime-${item?.movie?.id}`] || 0) && s.active)} data-content={label}>
					{label}
					<span>{label}</span>
				</div>
			);
		});
	}

	showTrailer(trailer, link, e) {
		e?.preventDefault?.();
		showTrailerModal({ link }, trailer);
		this.trackCarouselButtonClick(e);
	}

	renderButton(movie, button, i, buttons) {
		const label = button?.label;
		if (!label) return null;

		const className = joinClasses(s.button, button.type === 'primary' && s.primary);
		const destination = button.destination;

		if (isVideoAsset(destination)) {
			// Primary link of the item (e.g. get tickets), to also show it in the trailer overlay
			const mainLink = buttons[0] !== button ? buttons[0] : null;
			const onClick = (e) => this.showTrailer(destination, mainLink, e);

			if (isYoutubeLink(destination)) {

				// For youtube:id links (while we still support them), transform to proper clickable link
				let link = destination;
				if (link.startsWith('youtube:')) link = `https://www.youtube.com/watch?v=${getYoutubeId(link)}`;

				return <a class={className} href={link} target="_blank" rel="noopener noreferrer" onClick={onClick}>{label}</a>;
			}

			// For other video assets (e.g. mp4), render a button
			return <button class={className} onClick={onClick}>{label}</button>;
		}

		// Non-video link: open in new tab with proper security attributes
		return <a class={className} href={destination} target="_blank" rel="noopener noreferrer" onClick={this.trackCarouselButtonClick}>{label}</a>;
	}


	renderItem(item, i) {
		const movie = item.movie;
		if (!movie) return null;

		const onesheet = movie.onesheet;
		// Do not render item with bad images
		if (onesheet?.url && onesheetErrors.has(onesheet.url)) return null;

		const buttonsList = getButtons(item?.movie, item);
		// const url = item.link?.url || `https://www.universalstudios.com/movies/${movie.content?.slug || ''}`;
		const url = buttonsList[0]?.destination || `/movies/${movie.slug || ''}`;

		return (
			<div class={joinClasses(s.item, i === this.state.currentItem && s.current)} data-id={movie.id} key={movie.id}>
				<a class={s.posterWrapper} href={url} target="_blank" onClick={() => Store.emit(actions.CAROUSEL_TOUT_CLICK, { title: movie.title })}>
					<img class={s.poster} src={onesheet?.url} alt={onesheet?.alt || `${movie.title} poster image`} width="1050" height="1551" loading="lazy" onError={this.onOnesheetError} />
				</a>
				<div class={s.details}>
					{/* Have to use an extra wrapper to let the image control the width - is there a better way ? */}
					<div>
						<a class={s.name} href={url} target="_blank" onClick={() => Store.emit(actions.CAROUSEL_TOUT_CLICK, { title: movie.title })}>
							<span>{movie.title}</span>
							<ArrowRight class={s.arrow} />
						</a>
						<div class={s.showtimes} onAnimationEnd={this.onShowtimesAnimationEnd} onTransitionEnd={this.onShowtimesTransitionEnd}>{this.getNextShowtimes(item)}</div>
						<div class={s.buttons}>
							{buttonsList.map(this.renderButton.bind(this, movie))}
						</div>
					</div>
				</div>
			</div>
		);
	}

}